import type { Variants } from "motion/react";
import { durations, easings } from "./config";

// Fade with directional slide
export const fadeSlideUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: durations.normal, ease: easings.automotive },
  },
};

/**
 * A slab of the "why import" band arriving along its seam: it slides in from
 * the left with a slight lean that straightens as it lands, so the row reads
 * as a chain being pulled into place (2026-09-14).
 */
export const slideSeam: Variants = {
  hidden: { opacity: 0, x: -40, skewX: -8 },
  visible: {
    opacity: 1,
    x: 0,
    skewX: 0,
    transition: { duration: durations.slow, ease: easings.automotive },
  },
};

export const fadeSlideDown: Variants = {
  hidden: { opacity: 0, y: -24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: durations.normal, ease: easings.automotive },
  },
};

export const fadeSlideLeft: Variants = {
  hidden: { opacity: 0, x: 24 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: durations.normal, ease: easings.automotive },
  },
};

export const fadeSlideRight: Variants = {
  hidden: { opacity: 0, x: -24 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: durations.normal, ease: easings.automotive },
  },
};

// Scale animations
export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: durations.normal, ease: easings.automotive },
  },
};

export const scaleInBounce: Variants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: durations.slow, ease: easings.bounce },
  },
};

// Stagger container - use with children that have variants
export const staggerContainer: Variants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: durations.stagger,
      delayChildren: 0.1,
    },
  },
};

export const staggerContainerFast: Variants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.05,
    },
  },
};

// Hero-specific animations
export const heroTextReveal: Variants = {
  hidden: { opacity: 0, y: 30, filter: "blur(4px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: durations.slow, ease: easings.cruise },
  },
};

export const heroImageKenBurns: Variants = {
  hidden: { scale: 1.1 },
  visible: {
    scale: 1,
    transition: { duration: durations.kenBurns, ease: easings.cruise },
  },
};

// Card hover effects
export const cardHover = {
  rest: { y: 0, scale: 1 },
  hover: {
    y: -8,
    scale: 1.02,
    transition: { duration: durations.fast, ease: easings.aggressive },
  },
};

export const cardImageHover = {
  rest: { scale: 1 },
  hover: {
    scale: 1.05,
    transition: { duration: durations.normal, ease: easings.cruise },
  },
};

// Button interactions
export const buttonTap = {
  scale: 0.97,
  transition: { duration: 0.1 },
};

export const buttonHover = {
  scale: 1.03,
  transition: { duration: durations.fast, ease: easings.aggressive },
};

// Badge/pill slide in
export const badgeSlideIn: Variants = {
  hidden: { opacity: 0, x: 20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: durations.normal, ease: easings.automotive },
  },
};

// Accordion animations
export const accordionContent: Variants = {
  hidden: { opacity: 0, height: 0 },
  visible: {
    opacity: 1,
    height: "auto",
    transition: { duration: 0.3, ease: easings.automotive },
  },
};

export const accordionIcon: Variants = {
  closed: { rotate: 0 },
  open: { rotate: 45 },
};

// Blog card - subtle, editorial feel
export const blogCardHover = {
  rest: { y: 0 },
  hover: {
    y: -4,
    transition: { duration: durations.fast, ease: easings.automotive },
  },
};

export const blogImageReveal = {
  rest: { scale: 1, filter: 'brightness(1)' },
  hover: {
    scale: 1.03,
    filter: 'brightness(1.1)',
    transition: { duration: durations.normal, ease: easings.cruise },
  },
};

export const accentLineExpand = {
  rest: { width: '0%' },
  hover: {
    width: '100%',
    transition: { duration: durations.normal, ease: easings.automotive },
  },
};

// Proposal card - automotive, aggressive
export const proposalCardHover = {
  rest: {
    y: 0,
    boxShadow: '0 0 0 0 rgba(251, 192, 11, 0)',
  },
  hover: {
    y: -6,
    boxShadow: '0 8px 30px -8px rgba(251, 192, 11, 0.3)',
    transition: { duration: durations.fast, ease: easings.aggressive },
  },
};

export const proposalImageHover = {
  rest: { scale: 1 },
  hover: {
    scale: 1.08,
    transition: { duration: durations.normal, ease: easings.cruise },
  },
};

// Timeline animations
export const timelineLineDraw: Variants = {
  hidden: { pathLength: 0 },
  visible: {
    pathLength: 1,
    transition: { duration: 1.5, ease: easings.cruise },
  },
};

export const stepNodeReveal: Variants = {
  hidden: { scale: 0, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { duration: durations.normal, ease: easings.bounce },
  },
};

export const stepCardSlideLeft: Variants = {
  hidden: { opacity: 0, x: -40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: durations.normal, ease: easings.automotive },
  },
};

export const stepCardSlideRight: Variants = {
  hidden: { opacity: 0, x: 40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: durations.normal, ease: easings.automotive },
  },
};

// Hero stat badge animations
export const statBadgeReveal: Variants = {
  hidden: { opacity: 0, scale: 0.9, y: 10 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: durations.normal, ease: easings.automotive },
  },
};

// Counter number animation
export const counterReveal: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: durations.slow, ease: easings.cruise },
  },
};
